import React, { useEffect, useRef, useState } from 'react';
import './YouTubeTile.css';

interface Channel {
  name: string;
  avatar: string;
  videoUrl: string;
}

// Matches .youtube-channel-visual.expanded's width in YouTubeTile.css —
// duplicated here since measuring the actual expanded box on hover would
// be a frame too late (it needs its final anchor before it's shown).
const EXPANDED_WIDTH = 400;

// How long the cursor has to sit on an avatar before it expands — long
// enough that just passing over several avatars on the way somewhere
// else doesn't fire a burst of iframe loads.
const HOVER_DELAY_MS = 700;

// Matches .youtube-channel-visual.expanded's transition duration in
// YouTubeTile.css — the iframe (autoplay=1, so it starts decoding/
// playing the instant it mounts) only swaps in once the box has finished
// growing, instead of loading a video *while* the box is still
// animating and fighting it for the frame budget.
const VIDEO_LOAD_DELAY_MS = 400;

const YouTubeTile: React.FC = () => {
  const [hoveredChannel, setHoveredChannel] = useState<number | null>(null);
  const [videoChannel, setVideoChannel] = useState<number | null>(null);
  const [expandAnchor, setExpandAnchor] = useState<'start' | 'center' | 'end'>('center');
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (videoTimeoutRef.current) clearTimeout(videoTimeoutRef.current);
    };
  }, []);

  // YouTube channels data
  const channels: Channel[] = [
    {
      name: 'David Imel',
      avatar: '/content/yt/davidimel.jpg',
      videoUrl: 'https://www.youtube.com/embed/Nmdqa82SVPY?autoplay=1&mute=1&controls=0&loop=1&playlist=Nmdqa82SVPY'
    },
    {
      name: 'Sidemen',
      avatar: '/content/yt/sidemen.jpg',
      videoUrl: 'https://www.youtube.com/embed/1yXTUQZALFE?autoplay=1&mute=1&controls=0&loop=1&playlist=1yXTUQZALFE'
    },
    {
      name: 'JunsKitchen',
      avatar: '/content/yt/junskitchen.jpg',
      videoUrl: 'https://www.youtube.com/embed/wAj3fltMu28?autoplay=1&mute=1&controls=0&loop=1&playlist=wAj3fltMu28'
    },
    {
      name: '야미보이 Yummyboy',
      avatar: '/content/yt/yummyboy.jpg',
      videoUrl: 'https://www.youtube.com/embed/Is7wqHtZlw8?autoplay=1&mute=1&controls=0&loop=1&playlist=Is7wqHtZlw8'
    },
    {
      name: 'Speeed',
      avatar: '/content/yt/speeed.jpg',
      videoUrl: 'https://www.youtube.com/embed/10x32_0zvIA?autoplay=1&mute=1&controls=0&loop=1&playlist=10x32_0zvIA'
    },
    {
      name: 'Top Gear Classics',
      avatar: '/content/yt/top gear.jpg',
      videoUrl: 'https://www.youtube.com/embed/deubQ5SlKYA?autoplay=1&mute=1&controls=0&loop=1&playlist=deubQ5SlKYA'
    },
    {
      name: 'Fern',
      avatar: '/content/yt/fern.jpg',
      videoUrl: 'https://www.youtube.com/embed/F5TERw9-vO4?autoplay=1&mute=1&controls=0&loop=1&playlist=F5TERw9-vO4'
    },
    {
      name: 'Mustard',
      avatar: '/content/yt/mustard.jpg',
      videoUrl: 'https://www.youtube.com/embed/W1L1sU0uI0o?autoplay=1&mute=1&controls=0&loop=1&playlist=W1L1sU0uI0o'
    },
    {
      name: 'nigahiga',
      avatar: '/content/yt/nigahiga.jpg',
      videoUrl: 'https://www.youtube.com/embed/AJVpL6L7Zeo?autoplay=1&mute=1&controls=0&loop=1&playlist=AJVpL6L7Zeo'
    },
    {
      name: 'Selected',
      avatar: '/content/yt/selected.jpg',
      videoUrl: 'https://www.youtube.com/embed/bFehguB4WFI?autoplay=1&mute=1&controls=0&loop=1&playlist=bFehguB4WFI'
    }
  ];

  return (
    <div className="bento-item bento-youtube">
      <div className="widget-content youtube-widget">
        <div className="youtube-header">
          <h3 className="youtube-title">Something for while I eat</h3>
        </div>
        <div className="youtube-channels-grid">
          {channels.map((channel, index) => (
            <div
              key={index}
              className="youtube-channel-item"
              onMouseEnter={(e) => {
                if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

                // Which way the expanded box should anchor depends on how
                // close this item sits to the tile's own edge — not on
                // grid column index, which would need to know the live
                // column count (5/4/3/2 across breakpoints) to be
                // reliable. Measuring against the actual container works
                // the same at every width. Computed immediately (cheap,
                // no reason to delay it) even though the expand itself is
                // debounced below.
                const container = e.currentTarget.closest('.bento-youtube');
                if (container) {
                  const itemRect = e.currentTarget.getBoundingClientRect();
                  const containerRect = container.getBoundingClientRect();
                  const itemCenterX = itemRect.left + itemRect.width / 2;
                  const half = EXPANDED_WIDTH / 2;
                  if (itemCenterX - half < containerRect.left) {
                    setExpandAnchor('start');
                  } else if (itemCenterX + half > containerRect.right) {
                    setExpandAnchor('end');
                  } else {
                    setExpandAnchor('center');
                  }
                }

                hoverTimeoutRef.current = setTimeout(() => {
                  // Box starts growing now. The video itself doesn't load
                  // until VIDEO_LOAD_DELAY_MS later (below), once that
                  // growth animation has actually finished.
                  setHoveredChannel(index);
                  if (videoTimeoutRef.current) clearTimeout(videoTimeoutRef.current);
                  videoTimeoutRef.current = setTimeout(() => {
                    setVideoChannel(index);
                  }, VIDEO_LOAD_DELAY_MS);
                }, HOVER_DELAY_MS);
              }}
              onMouseLeave={() => {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                  hoverTimeoutRef.current = null;
                }
                if (videoTimeoutRef.current) {
                  clearTimeout(videoTimeoutRef.current);
                  videoTimeoutRef.current = null;
                }
                setHoveredChannel(null);
                setVideoChannel(null);
              }}
            >
              <div className={`youtube-channel-visual ${hoveredChannel === index ? `expanded expand-from-${expandAnchor}` : ''}`}>
                {videoChannel === index ? (
                  <iframe
                    src={channel.videoUrl}
                    title={channel.name}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="youtube-video-frame"
                  />
                ) : (
                  <img
                    src={channel.avatar}
                    alt={channel.name}
                    className="youtube-channel-avatar"
                  />
                )}
              </div>
              <div className="youtube-channel-info">
                <p className="youtube-channel-name">{channel.name}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default YouTubeTile;
